import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Adds every bundled product's currently selected variant (rendered by
 * bundle-item-picker elements elsewhere in the same section) to the cart in
 * one request, as separate line items — mirrors the Cart AJAX API's own
 * `items: [{id, quantity}, ...]` shape, already used elsewhere in this theme
 * (component-cart-items.js) for multi-item adds.
 *
 * The quantity + add-to-cart button are the theme's own reused sub-blocks
 * (content_for 'block', type: 'quantity'/'add-to-cart') — this element only
 * intercepts the wrapping <form>'s submit to redirect it to a multi-item add.
 */
class BundleAddToCart extends HTMLElement {
  #submitting = false;

  connectedCallback() {
    this.#form?.addEventListener('submit', this.#onSubmit);
  }

  disconnectedCallback() {
    this.#form?.removeEventListener('submit', this.#onSubmit);
  }

  get #form() {
    return this.querySelector('form');
  }

  get #quantity() {
    const value = Number(this.querySelector('input[name="quantity"]')?.value);
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  get #errorText() {
    return this.querySelector('[data-bundle-error-text]');
  }

  get #errorWrapper() {
    return this.querySelector('[data-bundle-error]');
  }

  get #liveRegion() {
    return this.querySelector('[data-bundle-live-region]');
  }

  /**
   * @returns {{id: string, quantity: number, available: boolean}[]}
   */
  #getItems() {
    // bundle-variant-picker and bundle-buy-buttons are sibling blocks, not nested — the
    // item-picker inputs live outside this element's own subtree. Scope to the closest
    // .shopify-section on the real PDP; inside the quick-add modal there is no
    // .shopify-section ancestor (content is injected into a plain div), so fall back to
    // the dialog, matching the pattern used by product-form.js/product-inventory.js.
    const scope = this.closest('.shopify-section') || this.closest('dialog') || this;

    const quantity = this.#quantity;

    return Array.from(scope.querySelectorAll('[data-bundle-item-input]')).map((input) => {
      const wrapper = input.closest('bundle-item-picker');

      return {
        id: input instanceof HTMLInputElement ? input.value : '',
        quantity,
        available: wrapper?.dataset.available !== 'false',
      };
    });
  }

  /**
   * @param {SubmitEvent} event
   */
  #onSubmit = async (event) => {
    event.preventDefault();

    // Guard against double-submits with a plain flag rather than the
    // button's `disabled` attribute — add-to-cart-button.liquid's own CSS
    // renders `:disabled` as its yellow "unavailable / notify me" state,
    // which would otherwise flash on the button for every real add.
    if (this.#submitting) return;

    const items = this.#getItems();
    const isUnavailable = items.length === 0 || items.some((item) => !item.id || !item.available);

    if (isUnavailable) {
      this.#showError(this.dataset.unavailableText || 'One or more items in this bundle are currently unavailable.');
      return;
    }

    this.#submitting = true;

    try {
      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sectionsToUpdate = new Set();
      cartItemsComponents.forEach((element) => {
        if (element instanceof HTMLElement && element.dataset.sectionId) {
          sectionsToUpdate.add(element.dataset.sectionId);
        }
      });

      const body = JSON.stringify({
        items: items.map(({ id, quantity }) => ({ id, quantity })),
        sections: Array.from(sectionsToUpdate).join(','),
      });

      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
      const result = await response.json();

      if (result.status) {
        window.dispatchEvent(new CartErrorEvent(this.id, result.message));
        this.#showError(result.message);
        return;
      }

      this.#clearError();

      this.dispatchEvent(
        new CartAddEvent({}, this.id, {
          source: 'bundle-add-to-cart',
          itemCount: items.length,
          sections: result.sections,
        })
      );

      document.querySelector('cart-drawer-component')?.open();
    } catch (error) {
      console.error(error);
    } finally {
      this.#submitting = false;
    }
  };

  /**
   * @param {string} message
   */
  #showError(message) {
    const errorText = this.#errorText;
    if (errorText) errorText.textContent = message;

    this.#errorWrapper?.classList.remove('hidden');

    const liveRegion = this.#liveRegion;
    if (liveRegion) liveRegion.textContent = message;
  }

  #clearError() {
    this.#errorWrapper?.classList.add('hidden');

    const liveRegion = this.#liveRegion;
    if (liveRegion) liveRegion.textContent = '';
  }
}

if (!customElements.get('bundle-add-to-cart')) {
  customElements.define('bundle-add-to-cart', BundleAddToCart);
}
